import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { TeradataLinkedService, teradataAuthTypes } from 'app/model/linkedservice/teradata.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV, akvToResource, parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { isString } from 'app/shared/utils/string.util';
import { Constants } from 'app/shared/shared-constants';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AdapterContext, ITridentResource, ParameterConfig } from 'shell-models-lib';
import { connectionStringPartToResource, connectionStringPartToModel } from 'app/shared/utils/linkedservice-parameter.util';
import { ValueAdapter } from '../value.adapter';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { teradataAdditionalFieldsAllowedKeys, TeradataAdditionalPropertiesKeyType } from 'app/registries/consts/teradata-consts';
import { toParameterClass, toParameterObject } from './parameter-config.util';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TeradataLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                server: '*',
                authenticationType: '*',
                username: '*',
                password: 'secret',
                connectionString: (_context: AdapterContext, _value: unknown, ls: TeradataLinkedService) => {
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor) {
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                encryptedCredential: '*',
                sslMode: '*',
                portNumber: '*',
                httpsPortNumber: '*',
                useDataEncryption: '*',
                characterSet: '*',
                maxRespSize: '*'
            },
            parameters: '*'
        }
    }
})
export class TeradataLinkedServiceAdapter extends LinkedServiceAdapter<TeradataLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => TeradataLinkedService {
        return TeradataLinkedService;
    }

    protected model(): IModel {
        return TeradataLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.TeradataLinkedServiceTypeProperties> => {
        const connectionDetails = JSON.parse(tridentResource.connectionDetails);
        return {
            server: connectionDetails.server
        };
    };

    protected async propertiesToResourceAsync(linkedService: TeradataLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.TeradataLinkedService;

        properties.type = Metamodel.modelFor(TeradataLinkedService).name;

        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
        }

        // it's new create or existing odbc resource, check IR version
        if (linkedService.version === driverVersions.v1 && !linkedService.isOldMashupResource) {
            linkedService.isMashupResource = false;
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('DbcName', connectionStringPartToResource(linkedService, TeradataLinkedService.SERVER));
            if (linkedService.authenticationType === teradataAuthTypes.Basic || linkedService.authenticationType === teradataAuthTypes.LDAP) {
                builder.add('UID', connectionStringPartToResource(linkedService, TeradataLinkedService.USER_NAME));
                if (!(linkedService.password instanceof AzureKeyVault) && linkedService.password) {
                    builder.add('PWD', connectionStringPartToResource(linkedService, TeradataLinkedService.PASSWORD));
                }
                if (linkedService.authenticationType === teradataAuthTypes.LDAP) {
                    builder.add('MechanismName', 'LDAP');
                }
            }
            if (linkedService.authenticationType === teradataAuthTypes.Windows) {
                builder.add('UseIntegratedSecurity', '1');
            }
            linkedService.additionalFields?.forEach((pair) => {
                builder.add(pair.key, pair.value || '');
            });
            const connectionString = this._connectionStringToResource(builder.connectionString);

            properties.typeProperties = {
                connectionString,
                username: linkedService.authenticationType === teradataAuthTypes.Windows
                        ? this.valueToResource(linkedService, TeradataLinkedService.USER_NAME)
                        : undefined,
                password: linkedService.authenticationType === teradataAuthTypes.Windows
                        ? parameterizablePasswordOrAKVtoSecretBase(linkedService, TeradataLinkedService.PASSWORD, this.context) as Model.SecretBase
                        : linkedService.password instanceof AzureKeyVault
                            ? akvToResource(linkedService.password, this.context)
                            : undefined,
            };
            return linkedServiceResource;

        }

        if (linkedService.version === driverVersions.v2) {
            properties.typeProperties = {
                server: this.valueToResource(linkedService, TeradataLinkedService.SERVER),
                authenticationType: linkedService.authenticationType,
                username: this.valueToResource(linkedService, TeradataLinkedService.USER_NAME),
                password: parameterizablePasswordOrAKVtoSecretBase(
                    linkedService,
                    TeradataLinkedService.PASSWORD,
                    this.context
                ) as Model.SecretBase,
                sslMode: this.valueToResource(linkedService, TeradataLinkedService.SSL_MODE)
            };

            if (linkedService.parameterizableAdditionalFields?.length > 0) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
                };
            }
        } else {
            properties.typeProperties = {
                server: linkedService.server,
                authenticationType: linkedService.authenticationType,
                username: linkedService.userName,
                password: passwordOrAKVtoSecretBase(linkedService.password, this.context)
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: TeradataLinkedService): Promise<TeradataLinkedService> {
        const prop = linkedServiceResource.properties as Model.TeradataLinkedService;
        linkedService.version = __IS_FABRIC_DPE__ ? driverVersions.v2 : driverVersions.v1;
        if (prop.typeProperties.connectionString == null) {
            if (prop.version === CONNECTOR_VERSION20 || __IS_FABRIC_DPE__) {
                linkedService.isLegacyConnector = false;
                linkedService.version = driverVersions.v2;
                ValueAdapter.toModel(
                    linkedService,
                    TeradataLinkedService.SERVER,
                    prop.typeProperties.server
                );

                ValueAdapter.toModel(
                    linkedService,
                    TeradataLinkedService.USER_NAME,
                    prop.typeProperties.username
                );

                parameterizableSecretBaseToModel(
                    linkedService,
                    TeradataLinkedService.PASSWORD,
                    prop.typeProperties.password,
                    this.context
                );

                ValueAdapter.toModel(
                    linkedService,
                    TeradataLinkedService.SSL_MODE,
                    prop.typeProperties.sslMode
                );

                linkedService.authenticationType = prop.typeProperties.authenticationType;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const additionalPropertiesObj: { [key: string]: any } = {};
                for (const propertyName in prop.typeProperties) {
                    if (teradataAdditionalFieldsAllowedKeys.includes(propertyName as TeradataAdditionalPropertiesKeyType)) {
                        additionalPropertiesObj[propertyName] =
                            prop.typeProperties[propertyName as keyof Model.TeradataLinkedServiceTypeProperties];
                    }
                }
                linkedService.parameterizableAdditionalFields = toParameterClass(
                    additionalPropertiesObj,
                    linkedService
                ) as Array<ParameterConfig<TeradataAdditionalPropertiesKeyType>>;
            } else {
                linkedService.isOldMashupResource = true;
                linkedService.isMashupResource = true;
                linkedService.server = prop.typeProperties.server;
                linkedService.authenticationType = prop.typeProperties.authenticationType;
                linkedService.userName = prop.typeProperties.username;
                linkedService.password = secretBaseToPasswordOrAKV(prop.typeProperties.password, true, this.context);
                this._logInfo('Hit old mashup teradata resource');
            }
        } else {
            // for odbc resource
            linkedService.isOldMashupResource = false;
            linkedService.isMashupResource = false;
            linkedService.isLegacyConnector = true;
            linkedService.authenticationType = prop.typeProperties.username ? teradataAuthTypes.Windows : teradataAuthTypes.Basic;

            if (isString(prop.typeProperties.connectionString)) {
                const builder = new DbConnectionStringBuilderService(true);
                builder.parse(prop.typeProperties.connectionString as string);

                connectionStringPartToModel(linkedService, TeradataLinkedService.SERVER, builder.getValue('DbcName', false) || '');
                connectionStringPartToModel(linkedService, TeradataLinkedService.USER_NAME, builder.getValue('UID', false) || '');
                connectionStringPartToModel(linkedService, TeradataLinkedService.PASSWORD, builder.getValue('PWD', false) || '', true);

                // handle LDAP auth type
                linkedService.mechanismName = builder.getValue('MechanismName', false) || '';
                if (linkedService.mechanismName === teradataAuthTypes.LDAP) {
                    linkedService.authenticationType = teradataAuthTypes.LDAP;
                }

                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['DbcName', 'UID', 'PWD', 'UseIntegratedSecurity', 'MechanismName']);

                if ((linkedService.authenticationType === teradataAuthTypes.Basic || linkedService.authenticationType === teradataAuthTypes.LDAP)
                    && prop.typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, TeradataLinkedService.PASSWORD, prop.typeProperties.password, this.context);
                }

            } else if (SecureStringReplacer.isSecureStringObject(prop.typeProperties.connectionString)) {
                linkedService.server = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                if (prop.typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, TeradataLinkedService.PASSWORD, prop.typeProperties.password, this.context, false);
                } else {
                    linkedService.password = Constants.maskedCredential;
                }

                // handle LDAP auth type
                linkedService.mechanismName = Constants.maskedCredential;
            }
            if (linkedService.authenticationType === teradataAuthTypes.Windows) {
                ValueAdapter.toModel(linkedService, TeradataLinkedService.USER_NAME, prop.typeProperties.username);
               parameterizableSecretBaseToModel(linkedService, TeradataLinkedService.PASSWORD, prop.typeProperties.password, this.context, true);
            }
        }

        return linkedService;
    }

    protected fullyByJsonEditorCustomizer(linkedServiceResource: Model.LinkedServiceResource): boolean {
        // if both password and linked service is an AKV, we should put it into JSON editor
        const prop = linkedServiceResource.properties as Model.TeradataLinkedService;
        return (prop?.typeProperties?.connectionString as Model.SecretBase)?.type === 'AzureKeyVaultSecret';
    }
}
